import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, MessageSquare, Settings as SettingsIcon, ArrowLeft, Database, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [demoRequests, setDemoRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [dbStats, setDbStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Check if user is super admin
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.is_super_admin) {
      toast.error('Super admin access required');
      navigate('/dashboard');
      return;
    }
    
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch demo requests
      try {
        const demoRes = await axios.get(`${API}/demo-requests`, { headers });
        setDemoRequests(demoRes.data || []);
      } catch (err) {
        console.error('Failed to load demo requests:', err);
        setDemoRequests([]);
      }

      // Fetch all users/admins
      try {
        const usersRes = await axios.get(`${API}/super-admin/all-users`, { headers });
        setAllUsers(usersRes.data || []);
      } catch (err) {
        console.error('Failed to load users:', err);
        setAllUsers([]);
      }

      // Fetch all contact requests (website leads)
      try {
        const contactRes = await axios.get(`${API}/super-admin/all-contact-requests`, { headers });
        setContactRequests(contactRes.data || []);
      } catch (err) {
        console.error('Failed to load contact requests:', err);
        setContactRequests([]);
      }

      // Fetch database statistics
      try {
        const statsRes = await axios.get(`${API}/super-admin/database/stats`, { headers });
        setDbStats(statsRes.data || {});
      } catch (err) {
        console.error('Failed to load database stats:', err);
        setDbStats({});
      }

      // Fetch pending users
      try {
        const pendingRes = await axios.get(`${API}/super-admin/pending-users`, { headers });
        setPendingUsers(pendingRes.data || []);
      } catch (err) {
        console.error('Failed to load pending users:', err);
        setPendingUsers([]);
      }
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load super admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDatabaseStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/database/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDbStats(response.data);
    } catch (error) {
      console.error('Failed to load database stats:', error);
      toast.error('Failed to load database statistics');
    }
  };

  const exportCollection = async (collectionName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/database/export/${collectionName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collectionName}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} ${collectionName} records`);
    } catch (error) {
      console.error(`Failed to export ${collectionName}:`, error);
      toast.error(`Failed to export ${collectionName}`);
    }
  };

  const updateDemoStatus = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/demo-requests/${requestId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteDemoRequest = async (requestId) => {
    if (!window.confirm('Delete this demo request?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/demo-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demo request deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const deleteAdmin = async (userId, userEmail) => {
    if (!window.confirm(`Delete admin ${userEmail} and ALL their clients? This cannot be undone!`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/super-admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const impersonateAdmin = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/super-admin/impersonate/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Save new token and user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('impersonated', 'true');
      localStorage.setItem('original_admin', response.data.original_admin);
      
      toast.success('Logged in as ' + response.data.user.email);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to impersonate admin');
    }
  };

  const resetPassword = async (userId, userEmail) => {
    const newPassword = window.prompt(`Enter new password for ${userEmail}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/super-admin/reset-password/${userId}`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const approveUser = async (userId, userName) => {
    if (!window.confirm(`Approve ${userName} for beta access?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/super-admin/approve-user/${userId}`,
        { action: 'approve' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${userName} approved! They will receive an email notification.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const rejectUser = async (userId, userName) => {
    if (!window.confirm(`Reject ${userName}'s beta application?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/super-admin/approve-user/${userId}`,
        { action: 'reject' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${userName}'s application rejected. They will receive an email notification.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Impersonation Banner */}
      {localStorage.getItem('impersonated') === 'true' && (
        <div className="bg-yellow-400 text-black px-4 py-2 text-center font-semibold">
          ⚠️ You are impersonating another admin ({localStorage.getItem('original_admin')})
          <button
            onClick={() => {
              localStorage.removeItem('impersonated');
              localStorage.removeItem('original_admin');
              window.location.href = '/login';
            }}
            className="ml-4 underline"
          >
            Exit Impersonation
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="beta-approvals" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="beta-approvals">Beta Approvals ({pendingUsers?.length || 0})</TabsTrigger>
            <TabsTrigger value="demo-requests">Demo Requests ({demoRequests?.length || 0})</TabsTrigger>
            <TabsTrigger value="website-leads">Website Leads ({contactRequests?.length || 0})</TabsTrigger>
            <TabsTrigger value="users">All Admins ({allUsers?.length || 0})</TabsTrigger>
            <TabsTrigger value="database">Database</TabsTrigger>
            <TabsTrigger value="settings">System Settings</TabsTrigger>
          </TabsList>

          {/* Beta Approvals Tab */}
          <TabsContent value="beta-approvals">
            <Card>
              <CardHeader>
                <CardTitle>Beta Signup Approvals ({pendingUsers?.length || 0})</CardTitle>
                <CardDescription>Review and approve users who have signed up for private beta access</CardDescription>
              </CardHeader>
              <CardContent>
                {!pendingUsers || pendingUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No pending approvals</p>
                    <p className="text-sm mt-2">All beta signups have been reviewed</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingUsers.map((user) => (
                      <div key={user.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div className="space-y-1">
                            <p className="font-medium text-lg">{user.first_name} {user.last_name}</p>
                            <p className="text-sm text-gray-600">📧 {user.email}</p>
                            <p className="text-sm text-gray-600">👤 Username: {user.username}</p>
                            {user.company_name && (
                              <p className="text-sm text-gray-600">🏢 {user.company_name}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                              Signed up: {new Date(user.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => approveUser(user.id, `${user.first_name} ${user.last_name}`)}
                            >
                              ✓ Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => rejectUser(user.id, `${user.first_name} ${user.last_name}`)}
                            >
                              ✗ Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Demo Requests Tab */}
          <TabsContent value="demo-requests">
            <Card>
              <CardHeader>
                <CardTitle>Demo Requests ({demoRequests?.length || 0})</CardTitle>
                <CardDescription>Manage demo requests from the homepage</CardDescription>
              </CardHeader>
              <CardContent>
                {!demoRequests || demoRequests.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No demo requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {demoRequests.map((request) => (
                      <div key={request.id} className="border rounded-lg p-4 space-y-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{request.name}</p>
                            <p className="text-sm text-gray-600">{request.email}</p>
                            <p className="text-sm text-gray-600">Company: {request.company}</p>
                          </div>
                          <div className="flex gap-2">
                            <select
                              value={request.status}
                              onChange={(e) => updateDemoStatus(request.id, e.target.value)}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="demo-scheduled">Demo Scheduled</option>
                              <option value="converted">Converted</option>
                              <option value="declined">Declined</option>
                            </select>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteDemoRequest(request.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Website Leads Tab */}
          <TabsContent value="website-leads">
            <Card>
              <CardHeader>
                <CardTitle>Website Leads ({contactRequests?.length || 0})</CardTitle>
                <CardDescription>Contact form submissions from all chatbots</CardDescription>
              </CardHeader>
              <CardContent>
                {!contactRequests || contactRequests.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No contact requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contactRequests.map((request) => (
                      <div key={request.id} className="border rounded-lg p-4 space-y-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{request.name}</p>
                            <p className="text-sm text-gray-600">{request.email}</p>
                            {request.phone && <p className="text-sm text-gray-600">{request.phone}</p>}
                            <p className="text-xs text-blue-600 mt-1">Chatbot: {request.client_name}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            request.status === 'new' ? 'bg-green-100 text-green-800' :
                            request.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm bg-gray-50 p-2 rounded">{request.message}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>All Admins ({allUsers?.length || 0})</CardTitle>
                <CardDescription>Manage all admin accounts and their clients</CardDescription>
              </CardHeader>
              <CardContent>
                {!allUsers || allUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No users found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allUsers.map((user) => (
                      <div key={user.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{user.username}</p>
                              {user.is_super_admin && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                  SUPER ADMIN
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <div className="flex gap-4 mt-2 text-sm text-gray-500">
                              <span>Clients: {user.client_count}</span>
                              <span>Joined: {new Date(user.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => impersonateAdmin(user.id)}
                              title="Login as this admin"
                            >
                              Login As
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resetPassword(user.id, user.email)}
                              title="Reset password"
                            >
                              Reset Password
                            </Button>
                            {!user.is_super_admin && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteAdmin(user.id, user.email)}
                                title="Delete admin and all their clients"
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Database Management Tab */}
          <TabsContent value="database">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="w-5 h-5" />
                      Database Management
                    </CardTitle>
                    <CardDescription>View, export, and manage database collections</CardDescription>
                  </div>
                  <Button onClick={fetchDatabaseStats} variant="outline" size="sm">
                    Refresh Stats
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Database Statistics */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4">Database Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Card className="border-2 border-blue-100">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-600">{dbStats.users || 0}</div>
                        <div className="text-sm text-gray-600">Users</div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-green-100">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-600">{dbStats.clients || 0}</div>
                        <div className="text-sm text-gray-600">Clients</div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-purple-100">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-purple-600">{dbStats.conversations || 0}</div>
                        <div className="text-sm text-gray-600">Conversations</div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-orange-100">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-orange-600">{dbStats.knowledge_entries || 0}</div>
                        <div className="text-sm text-gray-600">Knowledge Entries</div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-red-100">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-red-600">{dbStats.contact_requests || 0}</div>
                        <div className="text-sm text-gray-600">Contact Requests</div>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-indigo-100">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-indigo-600">{dbStats.demo_requests || 0}</div>
                        <div className="text-sm text-gray-600">Demo Requests</div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Export Collections */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Export Collections</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Download database collections as JSON files for backup or analysis.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Button
                      onClick={() => exportCollection('users')}
                      variant="outline"
                      className="justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Users ({dbStats.users || 0})
                    </Button>
                    <Button
                      onClick={() => exportCollection('clients')}
                      variant="outline"
                      className="justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Clients ({dbStats.clients || 0})
                    </Button>
                    <Button
                      onClick={() => exportCollection('conversations')}
                      variant="outline"
                      className="justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Conversations ({dbStats.conversations || 0})
                    </Button>
                    <Button
                      onClick={() => exportCollection('knowledge')}
                      variant="outline"
                      className="justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Knowledge ({dbStats.knowledge_entries || 0})
                    </Button>
                    <Button
                      onClick={() => exportCollection('contact-requests')}
                      variant="outline"
                      className="justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Contact Requests ({dbStats.contact_requests || 0})
                    </Button>
                    <Button
                      onClick={() => exportCollection('demo-requests')}
                      variant="outline"
                      className="justify-start"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Demo Requests ({dbStats.demo_requests || 0})
                    </Button>
                  </div>
                </div>

                {/* Database Management Info */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Database Management Notes:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Exports are in JSON format with timestamps in filename</li>
                    <li>• Data is exported from PostgreSQL database with proper formatting</li>
                    <li>• User passwords are excluded from exports for security</li>
                    <li>• Use these exports for backup, migration, or data analysis</li>
                    <li>• Large collections may take a moment to download</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>System Settings</CardTitle>
                <CardDescription>Configure development mode and limits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h3 className="font-medium mb-2">⚙️ Development Mode</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Control subscription limits and trial enforcement
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="disable-limits" className="w-4 h-4" />
                        <label htmlFor="disable-limits" className="text-sm">Disable message limits</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="disable-trials" className="w-4 h-4" />
                        <label htmlFor="disable-trials" className="text-sm">Disable trial expiration</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="disable-client-limits" className="w-4 h-4" />
                        <label htmlFor="disable-client-limits" className="text-sm">Disable client limits</label>
                      </div>
                    </div>
                    <Button className="mt-4">Save Settings</Button>
                  </div>
                  
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-medium mb-2">ℹ️ Environment</h3>
                    <p className="text-sm text-gray-600">
                      Current: <strong>Development</strong>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Change this in backend/.env (ENVIRONMENT=production)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
